import { useMemo } from 'react'

import { matchFilterKey, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { TeamMemberModel, TeamMemberRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['teamMembers']

type TeamMemberFilters = {
  team_id?: number,
}

type UseTeamMembersOptions = {
  filters?: TeamMemberFilters,
  performHttpRequests?: boolean,
  requestOptions?: TeamMemberRequestOptions,
}

function useTeamMembers(options: UseTeamMembersOptions) {
  const { filters = {}, requestOptions } = options
  const {
    team_id: filterTeamId,
  } = filters

  const {
    updatedEntities: { teamMembers: teamMembersUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { teamMembers } = useSelector(reduxState => reduxState.entities)

  const filteredTeamMembers = useMemo(() => {
    const filtered = Object.values(teamMembers).filter((teamMember: TeamMemberModel) => {
      const { team_id } = teamMember

      const matchTeamId = matchFilterKey(team_id, filterTeamId)

      return matchTeamId
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [teamMembersUpdatedAt, JSON.stringify(filters)])

  const filteredTeamMembersCount = filteredTeamMembers.length
  const hasFilteredTeamMembers = !!filteredTeamMembersCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredTeamMembersCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingTeamMembers } = useReduxAction(
    'teamMembers',
    'loadTeamMembers',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredTeamMembers,
    filteredTeamMembersCount,
    hasFilteredTeamMembers,
    loading: loadingTeamMembers,
  }
}

export default useTeamMembers
